import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ConfirmPlannedMovementRequest,
  CreatePlannedMovementRequest,
  PlannedMovement,
  PlannedMovementQuery,
  UpdatePlannedMovementRequest,
} from '@financial/shared';
import { api } from '../../lib/api';
import { notify } from '../../store/notifications';

const KEY = ['planned-movements'];

export function usePlanned(query: PlannedMovementQuery = {}) {
  return useQuery({
    queryKey: [...KEY, query],
    queryFn: async () => {
      const res = await api.get<PlannedMovement[]>('/planned-movements', { params: query });
      return res.data;
    },
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KEY });
    // Confirmar un planeado crea un movimiento real y mueve saldos.
    void qc.invalidateQueries({ queryKey: ['transactions'] });
    void qc.invalidateQueries({ queryKey: ['transfers'] });
    void qc.invalidateQueries({ queryKey: ['accounts'] });
    void qc.invalidateQueries({ queryKey: ['net-worth'] });
    void qc.invalidateQueries({ queryKey: ['budget'] });
    void qc.invalidateQueries({ queryKey: ['insights'] });
    // Un pendiente confirmado con tarjeta crea un cargo contado y actualiza
    // deuda, capacidad disponible y proyección del próximo corte.
    void qc.invalidateQueries({ queryKey: ['credit-card'] });
  };
}

export function useCreatePlanned() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (body: CreatePlannedMovementRequest) => {
      const res = await api.post<PlannedMovement>('/planned-movements', body);
      return res.data;
    },
    onSuccess: (planned) => {
      invalidate();
      notify.success('Pendiente creado', {
        action: {
          label: 'Deshacer',
          onClick: async () => {
            await api.delete(`/planned-movements/${planned.id}`);
            invalidate();
            notify.info('Pendiente deshecho');
          },
        },
      });
    },
  });
}

/** Un pendiente único puede corregirse antes de que se convierta en un movimiento real. */
export function useUpdatePlanned() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (vars: { id: string; body: UpdatePlannedMovementRequest }) => {
      const res = await api.patch<PlannedMovement>(`/planned-movements/${vars.id}`, vars.body);
      return res.data;
    },
    onSuccess: () => {
      invalidate();
      notify.success('Pendiente actualizado');
    },
  });
}

export function useConfirmPlanned() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (vars: { id: string; body: ConfirmPlannedMovementRequest }) => {
      const res = await api.post<PlannedMovement>(
        `/planned-movements/${vars.id}/confirm`,
        vars.body,
      );
      return res.data;
    },
    onSuccess: invalidate,
  });
}

export function useCancelPlanned() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<PlannedMovement>(`/planned-movements/${id}/cancel`, {});
      return res.data;
    },
    onSuccess: invalidate,
  });
}

export function useDeletePlanned() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/planned-movements/${id}`);
    },
    onSuccess: invalidate,
  });
}
